import React from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import "./EventEntry.css";

export interface EntryStep {
  key: string;
  chipLabel: string;
  /** The answer so far, or null while the step is still unanswered. */
  chipValue: string | null;
  prompt: string;
  render: () => React.ReactNode;
}

interface EntryPanelProps {
  title: string;
  subtitle: string;
  steps: EntryStep[];
  activeStepKey: string;
  onStepSelect: (key: string) => void;
  errorMessage: string | null;
  isSubmitting: boolean;
  canSave: boolean;
  saveLabel: string;
  onSave: () => void;
  onCancel: () => void;
}

/**
 * The shell shared by goal and penalty entry: one question at a time, with
 * every answer so far shown as a chip that jumps back to its own step. It
 * takes the place of the scoreboard rather than overlaying it, so a long
 * roster scrolls normally on a phone.
 */
function EntryPanel({
  title,
  subtitle,
  steps,
  activeStepKey,
  onStepSelect,
  errorMessage,
  isSubmitting,
  canSave,
  saveLabel,
  onSave,
  onCancel,
}: EntryPanelProps) {
  const activeStep =
    steps.find((step) => step.key === activeStepKey) ?? steps[0];

  return (
    <div className="ee-panel">
      <div className="ee-header">
        <h4 className="ee-title">{title}</h4>
        <p className="ee-subtitle">{subtitle}</p>
      </div>

      <div className="ee-chips">
        {steps.map((step) => {
          const isActive = step.key === activeStep.key;
          const className = [
            "ee-chip",
            isActive ? "ee-chip--active" : "",
            step.chipValue === null ? "ee-chip--empty" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <button
              key={step.key}
              type="button"
              className={className}
              aria-current={isActive}
              onClick={() => onStepSelect(step.key)}
            >
              <span className="ee-chip-label">{step.chipLabel}</span>
              <span className="ee-chip-value">
                {step.chipValue ?? "Not set"}
              </span>
            </button>
          );
        })}
      </div>

      {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}

      <h5 className="ee-prompt">{activeStep.prompt}</h5>
      {/* Keyed by step so a step that focuses a field does so each time it
          opens, not only the first time the panel mounts. */}
      <div key={activeStep.key}>{activeStep.render()}</div>

      <div className="ee-footer">
        <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onSave}
          disabled={isSubmitting || !canSave}
        >
          {isSubmitting ? (
            <>
              <Spinner animation="border" size="sm" /> Saving...
            </>
          ) : (
            saveLabel
          )}
        </Button>
      </div>
    </div>
  );
}

export default EntryPanel;
